import { Button, Modal, Progress, Table, Upload, message } from "antd";
import { useRef, useState } from "react";
import { ERROR_CODE, RELOAD_ERROR_CODES } from "features/ConsultRequest/core/constants";
import { formatDateTime, formatFileSize } from "features/ConsultRequest/core/utils";
import { deleteFile, downloadFile, listFiles, uploadFile } from "features/ConsultRequest/data/apiAdapters";
import { useConsultCall, useConsultQuery } from "features/ConsultRequest/data/hooks";
import ConsultErrorState from "./ConsultErrorState";

const EMPTY_TEXT = "Chưa có file nào. Tải lên sơ đồ mặt bằng, ảnh hiện trạng, dự toán… để lưu theo phiếu.";

export default function FilesTab({ requestId, permissions, onChanged, onStale }) {
  const { call } = useConsultCall();
  const files = useConsultQuery(listFiles, [requestId]);
  // uploads: [{uid, name, percent, error, file}]
  const [uploads, setUploads] = useState([]);
  const controllers = useRef({});

  const patch = (uid, change) => setUploads((list) => list.map((u) => (u.uid === uid ? { ...u, ...change } : u)));
  const drop = (uid) => setUploads((list) => list.filter((u) => u.uid !== uid));

  const start = async (file) => {
    const uid = file.uid;
    const controller = new AbortController();
    controllers.current[uid] = controller;
    setUploads((list) => [...list.filter((u) => u.uid !== uid), { uid, name: file.name, percent: 0, file }]);
    try {
      await call(uploadFile, requestId, file, {
        signal: controller.signal,
        onUploadProgress: (e) => patch(uid, { percent: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }),
      });
      drop(uid);
      await files.refetch();
      onChanged?.();
    } catch (e) {
      if (controller.signal.aborted) return drop(uid);
      if ([ERROR_CODE.FILE_TYPE_NOT_ALLOWED, ERROR_CODE.FILE_TOO_LARGE].includes(e.code))
        patch(uid, { error: e.message });
      else {
        drop(uid);
        message.error(e.message);
        if (RELOAD_ERROR_CODES.includes(e.code)) onStale?.();
      }
    }
    return undefined;
  };

  const remove = (file) =>
    Modal.confirm({
      title: `Xóa file "${file.fileName}"?`,
      okText: "Xóa",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          await call(deleteFile, requestId, file.id);
          await files.refetch();
          onChanged?.();
        } catch (e) {
          message.error(e.message);
          if (RELOAD_ERROR_CODES.includes(e.code)) onStale?.();
        }
      },
    });

  // Tải file qua GraphQL downloadFile (base64) thay cho downloadUrl REST.
  const download = async (event, file) => {
    event.preventDefault();
    try {
      const result = await call(downloadFile, requestId, file.id);
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || file.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      message.error(e.message);
    }
  };

  const columns = [
    { title: "TÊN FILE", dataIndex: "fileName" },
    { title: "NGƯỜI TẢI", width: 200, render: (_, f) => f.uploadedBy?.name },
    { title: "DUNG LƯỢNG", width: 120, render: (_, f) => formatFileSize(f.size) },
    { title: "NGÀY", width: 170, render: (_, f) => formatDateTime(f.createdAt, { seconds: false }) },
    {
      title: "HÀNH ĐỘNG",
      width: 160,
      render: (_, f) => (
        <>
          <a href={f.downloadUrl} onClick={(event) => download(event, f)}>
            Tải về
          </a>
          {f.canDelete && (
            <Button type="link" danger onClick={() => remove(f)}>
              Xóa
            </Button>
          )}
        </>
      ),
    },
  ];
  if (files.error) return <ConsultErrorState error={files.error} onRetry={files.refetch} />;
  return (
    <div>
      {permissions?.canUploadFile && (
        <Upload.Dragger
          multiple
          showUploadList={false}
          beforeUpload={(file) => {
            start(file);
            return false;
          }}
          style={{ marginBottom: 12 }}
        >
          ⤒ Tải lên tài liệu (chọn hoặc kéo thả nhiều file)
        </Upload.Dragger>
      )}
      {uploads.map((u) => (
        <div key={u.uid} style={{ background: u.error ? "#FFF1F0" : undefined, padding: 8 }}>
          <span>{u.name}</span>
          {u.error ? (
            <>
              <span style={{ color: "#D8001A", marginLeft: 8 }}>{u.error}</span>
              <Button type="link" onClick={() => start(u.file)}>
                Thử lại
              </Button>
              <Button type="link" onClick={() => drop(u.uid)}>
                Bỏ
              </Button>
            </>
          ) : (
            <>
              <Progress percent={u.percent} size="small" />
              <Button type="link" onClick={() => controllers.current[u.uid]?.abort()}>
                Hủy
              </Button>
            </>
          )}
        </div>
      ))}
      <Table
        rowKey="id"
        loading={files.loading}
        columns={columns}
        dataSource={files.data?.items || []}
        pagination={false}
        locale={{ emptyText: EMPTY_TEXT }}
      />
    </div>
  );
}
